import React, { useState, useEffect, useRef } from 'react';
import StepProgressBar from './StepProgressBar';
import UserAuthCard from './Components/UserAuthCard';
import VerifyIdentityCard from './Components/VerifyIdentityCard';
import SelectModelCard from './Components/SelectModelCard';
import CallSubmittedCard from './Components/CallSubmittedCard';
import InitiateCallCard from './Components/InitiateCallCard';
import CultService from '../../../../../Services/CultService';
import toast from 'react-hot-toast';
import { Badge, Box, Card, Chip, Stack, Typography } from '@mui/material';
import { InfoIcon } from '../../../../../Components/Icons/Icons';
import { useNavigate, useParams } from 'react-router-dom';
import { throttle } from 'lodash';

// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

const InfoCard = () => {
	return (
		<Card
			sx={{
				mt: { xs: 2, md: 4 },
				width: '100%',
				borderRadius: 3,
				boxShadow: 2,
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'start',
				padding: 2,
				gap: 1, // Space between the icon and text
			}}
		>
			{/* Left Side: Info Icon */}
			<Box>
				<InfoIcon size={24} /> {/* Adjust size and color as needed */}
			</Box>

			{/* Right Side: Typography */}
			<Box>
				<Typography
					sx={{
						fontWeight: 600,
						fontStyle: 'SemiBold',
						fontSize: { xs : '12px', md : '14px' },
						lineHeight: '150%',
						letterSpacing: '0%',
						color: '#333333', // Adjust color as needed
					}}
				>
					What happens next?
				</Typography>
				<Typography
					sx={{
						fontWeight: 400,
						fontStyle: 'Regular',
						fontSize: { xs : '12px', md : '14px' },
						lineHeight: '150%',
						letterSpacing: '0%',
						color: '#777777', // Adjust color as needed
					}}
				>
					You'll receive a call within 2-3 minutes. Please keep your phone nearby and answer when we call.
				</Typography>
			</Box>
		</Card>
	);
};

// Company ID - replace with dynamic method if needed

// const COMPANY_ID = "682b680a4c17b11dd97b743c";

export const CallProcessFlow = () => {
	const { id } = useParams();
	let COMPANY_ID = id;
	const [activeIndex, setActiveIndex] = useState(0);
	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [otp, setOtp] = useState('');
	const [assistants, setAssistants] = useState([]);
	const [selectedAssistant, setSelectedAssistant] = useState(null);
	const [employee, setEmployee] = useState('');
	const [otpModalOpen, setOtpModalOpen] = useState(false);

	const throttledSendOTPRef = useRef();
	const throttledVerifyOTPRef = useRef();

	useEffect(() => {
		// Throttled Send OTP function
		throttledSendOTPRef.current = throttle(
			async (emailValue) => {
				try {
					setLoading(true);
					setEmail(emailValue);
					const response = await CultService.sendOTP({
						data: {
							email: emailValue,
							companyId: COMPANY_ID,
							purpose: 'incoming_call',
						},
					});

					console.log('Send OTP response:', response);

					if (response && response.status === 200) {
						toast.success(response.data.message || 'OTP sent successfully!');
						// Open the OTP verification modal instead of advancing to the next step
						setOtpModalOpen(true);
					}
				} catch (error) {
					console.error('Error sending OTP:', error);
					toast.error(error.response?.data?.error || 'Failed to send OTP');
					if(error.response?.status === 429) {
						setOtpModalOpen(false);
						setActiveIndex(0); // Move back to the User Authentication step
					}
				} finally {
					setLoading(false);
				}
			},
			5000,
			{ trailing: false },
		);

		// Throttled Verify OTP function
		throttledVerifyOTPRef.current = throttle(
			async (otpValue) => {
				try {
					setLoading(true);
					setOtp(otpValue);
					const response = await CultService.verifyOTP({
						data: {
							email,
							otp: otpValue,
							companyId: COMPANY_ID,
							purpose: 'incoming_call',
						},
					});

					console.log('Verify OTP response:', response);
					if (response && response.status === 200) {
						console.log('OTP verified successfully:', response.data);
						setEmployee(response.data.data.user);
						toast.success(response.data.message || 'Identity verified successfully!');

						// Close the modal and advance to the next step
						setOtpModalOpen(false);
						setActiveIndex(1); // Move to the Select Model step
					}
				} catch (error) {
					console.error('Error verifying OTP:', error);
					toast.error(error.response?.data?.error || 'Failed to verify OTP');
					if(error.response?.status === 429) {
						setOtpModalOpen(false);
						setActiveIndex(0); // Move back to the User Authentication step
					}
					
				} finally {
					setLoading(false);
				}
			},
			5000,
			{ trailing: false },
		);
	}, [COMPANY_ID, email]);
	// console.log("data..." , { assistants , selectedAssistant});

	// Fetch assistants when reaching the select model step
	useEffect(() => {
		if (activeIndex === 1) {
			fetchAssistants();
		}
	}, [activeIndex]);

	// Function to fetch available assistants
	const fetchAssistants = async () => {
		try {
			setLoading(true);
			const response = await CultService.getIncomingCallAssistants({ params: { companyId: COMPANY_ID } });
			console.log('Fetch assistants response:', response);
			if (response.status === 200) {
				const assistantOptions = response.data.data.incomingCallAssistants.map((assistant) => ({
					value: assistant.id,
					label: assistant?.agentDetails?.agentName || assistant?.name || assistant.id,
					details: { 'shortDescription' : assistant?.agentDetails?.shortDescription , 'longDescription' : assistant?.agentDetails?.longDescription, },
				}));
				setAssistants(assistantOptions);
			}
		} catch (error) {
			console.error('Error fetching assistants:', error);
			toast.error('Failed to fetch AI assistants');
		} finally {
			setLoading(false);
		}
	};

	// Function to handle sending OTP
	const handleSendOTP = (emailValue) => {
		if (loading) return; // Prevent execution if already loading
		throttledSendOTPRef.current(emailValue);
	};

	// Function to handle verifying OTP

	const handleVerifyOTP = (otpValue) => {
		if (loading) return; // Prevent execution if already loading
		throttledVerifyOTPRef.current(otpValue);
	};

	// Function to handle selecting a model
	const handleSelectModel = (assistantId) => {
		let selectedAssistantFromAllModels = assistants.find((assistant) => assistant.value === assistantId);
		setSelectedAssistant(selectedAssistantFromAllModels);
		setActiveIndex(2); // Since we now have one less step in the main flow
	};

	// Function to handle initiating a call
	const handleInitiateCall = async (phone) => {
		try {
			setLoading(true);
			const response = await CultService.initiateIncomingCall({
				data: {
					email,
					phoneNumber: phone,
					assistantId: selectedAssistant?.value,
				},
			});
			console.log('Call initiated response:', response, response?.data?.success);
			if (response?.data?.success) {
				toast.success(response?.data?.message || 'Call initiated successfully!');
				setPhoneNumber(phone);
				setActiveIndex(3);
			}
		} catch (error) {
			console.error('Error initiating call:', error);
			toast.error(error.response?.data?.error || 'Failed to initiate call');
			setActiveIndex(0); 
		} finally {
			setLoading(false);
		}
	};

	const navigate = useNavigate();


	const steps = [
		{
			label: 'User Authentication',
			Component: UserAuthCard,
			props: {
				onBack: () => navigate(-1), // This will go back to the previous page
				onNext: handleSendOTP,
				loading,
				email: email,
			},
		},
		{
			label: 'Select Model',
			Component: SelectModelCard,
			props: {
				models: assistants,
				onBack: () => setActiveIndex(0), // Go back to first step
				onSelect: handleSelectModel,
				loading,
			},
		},
		{
			label: 'Initiate Call',
			Component: InitiateCallCard,
			props: {
				onBack: () => setActiveIndex((prev) => prev - 1),
				onCall: handleInitiateCall,
				selectedAssistant: selectedAssistant?.label,
				selectedAssistantId: selectedAssistant?.value,
				employee,
				loading,
			},
		},
		{
			label: 'Call Submitted',
			Component: CallSubmittedCard,
			props: {
				selectedAssistant: selectedAssistant?.label,
				selectedAssistantId: selectedAssistant?.value,
				employee,
				phoneNumber,
			},
		},
	];

	return (
		<Box sx={{  width: { sm :  '90vw'  , md: '670px'}, display: 'flex', flexDirection: 'column', alignItems: 'center', mx: 'auto' , px: {xs : 1 , md : 2} , spaceing: 2 , mt : (activeIndex === 2 || activeIndex === 1) ? '26px' : '0px' }} >
			{/* Main Flow */}

			{!otpModalOpen && (
				<>
					{!(activeIndex === 3) && <StepProgressBar activeStep={activeIndex} totalSteps={steps.length} />}

					{steps.map(({ Component, props }, index) => (
						<Stack width={'100%'} key={index}>{activeIndex === index && <Component {...props} />}</Stack>
					))}

					{activeIndex === 2 && <InfoCard />}
				</>
			)}

			{/* OTP Verification Modal */}
			<VerifyIdentityCard
				open={otpModalOpen}
				onClose={() => !loading && setOtpModalOpen(false)}
				onVerify={handleVerifyOTP}
				onResendOtp={() => handleSendOTP(email)}
				email={email}
				loading={loading}
			/>
		</Box>
	);
};
